// Quest definitions for Valentine Farm Game

export interface QuestOption {
    value: string;
    label: string;
    icon?: string;
}

export interface Quest {
    key: string;
    chapter: 1 | 2 | 3;
    title: string;
    description: string;
    assistantMessage: string;
    options: QuestOption[];
    hasTextInput?: boolean;
    textInputLabel?: string;
    condition?: {
        questionKey: string;
        answerValue: string | string[];
    };
    buildingSlot?: string;
    buildingType?: string;
    decoration?: string;
    multiSelect?: boolean;
    disabled?: boolean;
}

export const QUESTS: Quest[] = [
    // Chapter 1: Vibe & World Setup
    {
        key: 'valentine',
        chapter: 1,
        title: 'Will You Be My Valentine?',
        description: 'The most important question of all... 💕',
        assistantMessage: "Ooh, exciting! Let's see what you say!",
        options: [
            { value: 'yes', label: 'Yes! ✅💖', icon: '❤️' },
            { value: 'no', label: 'No 😅', icon: '💔' },
        ],
    },
    {
        key: 'season',
        chapter: 1,
        title: 'The General Vibe',
        description: 'What kind of atmosphere do you want?',
        assistantMessage: 'Setting up the perfect vibe for our town!',
        options: [
            { value: 'soft/cozy', label: 'Soft & Cozy 🧸', icon: '🌸' },
            { value: 'fun/loud', label: 'Fun & Loud 🎉', icon: '🎊' },
            { value: 'romantic/calm', label: 'Romantic & Calm 💕', icon: '🕯️' },
        ],
        decoration: 'love_tree',
    },
    {
        key: 'vibe',
        chapter: 1,
        title: 'Indoor or Outdoor?',
        description: 'Where should we spend most of our time?',
        assistantMessage: 'This will shape our whole town layout!',
        options: [
            { value: 'indoors', label: 'Indoors 🏠', icon: '🏠' },
            { value: 'outdoors', label: 'Outdoors 🌳', icon: '🌳' },
            { value: 'mix', label: 'A Mix of Both 🌈', icon: '🌈' },
        ],
        buildingSlot: 'B',
        buildingType: 'park',
    },
    {
        key: 'music',
        chapter: 1,
        title: 'Background Music',
        description: "What should be playing?",
        assistantMessage: 'Building the music stage!',
        multiSelect: true,
        options: [
            { value: 'pop', label: 'Pop 🎤', icon: '🎵' },
            { value: 'r&b', label: 'R&B 💕', icon: '🎶' },
            { value: 'hip-hop', label: 'Hip-Hop 🔥', icon: '🎤' },
            { value: 'afrobeats', label: 'Afrobeats 🥁', icon: '🌍' },
            { value: 'latin', label: 'Latin 💃', icon: '🎺' },
            { value: 'jazz', label: 'Jazz 🎷', icon: '🎹' },
            { value: 'classical', label: 'Classical 🎻', icon: '🎼' },
            { value: 'k-pop', label: 'K-Pop 🌸', icon: '⭐' },
            { value: 'rock', label: 'Rock 🎸', icon: '🤘' },
            { value: 'country', label: 'Country 🤠', icon: '🎸' },
            { value: 'you choose', label: 'You Choose! 🎧', icon: '💭' },
        ],
        decoration: 'music_stage',
    },
    {
        key: 'outfitTheme',
        chapter: 1,
        title: 'The Dress Code',
        description: "How are we stepping out?",
        assistantMessage: 'Fashion is essential! Adding the wardrobe!',
        options: [
            { value: 'casual cute', label: 'Casual Cute 👚', icon: '👟' },
            { value: 'dressy', label: 'Dressy ✨', icon: '👗' },
            { value: 'matching colors', label: 'Matching Colors! 💑', icon: '💜' },
        ],
        decoration: 'wardrobe',
    },
    {
        key: 'photoMode',
        chapter: 1,
        title: 'Pictures?',
        description: 'How many photos are we taking?',
        assistantMessage: 'Capturing memories is important!',
        options: [
            { value: 'take pics', label: 'Take All the Pics! 📸', icon: '📷' },
            { value: 'few pics', label: 'Just a Few 🤳', icon: '📱' },
            { value: 'no pics', label: 'Live in the Moment 🙈', icon: '👀' },
        ],
        decoration: 'photo_booth',
    },

    // Chapter 2: Date Route
    {
        key: 'timeOfDay',
        chapter: 2,
        title: 'Day or Night?',
        description: 'When does the fun start?',
        assistantMessage: "Let's plan the timing!",
        options: [
            { value: 'day', label: 'Daytime ☀️', icon: '🌤️' },
            { value: 'night', label: 'Nighttime 🌙', icon: '🌙' },
            { value: 'whole day', label: 'The Whole Day! 🌅', icon: '✨' },
        ],
    },
    {
        key: 'food',
        chapter: 2,
        title: 'What Are We Eating?',
        description: 'Pick the main course.',
        assistantMessage: 'Building our restaurant!',
        multiSelect: true,
        options: [
            { value: 'local', label: 'Local Cuisine 🍲', icon: '🍛' },
            { value: 'continental', label: 'Continental 🍝', icon: '🍽️' },
            { value: 'asian_fusion', label: 'Asian Fusion 🍜', icon: '🥢' },
            { value: 'surprise me', label: 'Surprise Me! 🎁', icon: '🎀' },
        ],
        buildingSlot: 'C',
        buildingType: 'restaurant',
    },
    {
        key: 'dessert',
        chapter: 2,
        title: 'Dessert Choice',
        description: 'Any sweet cravings?',
        assistantMessage: 'Adding the dessert cart!',
        multiSelect: true,
        options: [
            { value: 'ice cream', label: 'Ice Cream 🍦', icon: '🍨' },
            { value: 'waffles', label: 'Waffles 🧇', icon: '🍫' },
            { value: 'cake', label: 'Cake 🎂', icon: '🍰' },
            { value: 'any sweet', label: 'Any Sweet! 🍬', icon: '🍭' },
        ],
        decoration: 'dessert_cart',
    },
    {
        key: 'activity',
        chapter: 2,
        title: 'Main Activity',
        description: "What are we doing?",
        assistantMessage: 'Building our activity spot!',
        multiSelect: true,
        options: [
            { value: 'cinema', label: 'Cinema 🎬', icon: '🎥' },
            { value: 'arcade', label: 'Arcade 🕹️', icon: '👾' },
            { value: 'bowling', label: 'Bowling 🎳', icon: '🎯' },
            { value: 'paint&sip', label: 'Paint & Sip 🎨', icon: '🖌️' },
            { value: 'beach', label: 'Beach 🏖️', icon: '🌊' },
            { value: 'games night', label: 'Games Night 🃏', icon: '🎲' },
        ],
        buildingSlot: 'D',
        buildingType: 'activity',
    },
    {
        key: 'transport',
        chapter: 2,
        title: 'Transportation',
        description: 'How do we get there?',
        assistantMessage: 'Wheels sorted!',
        options: [
            { value: 'drive me', label: 'Drive Me! 🚗', icon: '🚙' },
            { value: 'rideshare', label: 'Rideshare / Taxi 📱', icon: '🚕' },
            { value: 'public transit', label: 'Public Transit 🚇', icon: '🚌' },
            { value: 'decide together', label: 'Decide Together 🤝', icon: '💬' },
        ],
    },
    {
        key: 'location',
        chapter: 2,
        title: 'Location Preference',
        description: 'Stay local or travel somewhere?',
        assistantMessage: 'Planning our destination!',
        options: [
            { value: 'stay local', label: 'Stay Local 🏙️', icon: '🌆' },
            { value: 'travel', label: 'Travel! ✈️', icon: '🧳' },
            { value: 'either is fine', label: 'Either is Fine 🤷', icon: '💖' },
        ],
        buildingSlot: 'E',
        buildingType: 'station',
    },

    // Chapter 3: Soft-Life Add-ons
    {
        key: 'stayType',
        chapter: 3,
        title: 'Accommodation',
        description: 'Where are we staying?',
        assistantMessage: 'Building your home base!',
        options: [
            { value: 'home', label: 'Home Sweet Home 🏠', icon: '🏡' },
            { value: 'airbnb', label: 'Airbnb 🏕️', icon: '🛖' },
            { value: 'hotel', label: 'Hotel 🏨', icon: '✨' },
            { value: 'day-only', label: 'Day Only ☀️', icon: '🌞' },
        ],
        buildingSlot: 'A',
        buildingType: 'stay',
    },
    {
        key: 'surpriseTolerance',
        chapter: 3,
        title: 'Surprises?',
        description: 'Do you want surprises along the way?',
        assistantMessage: 'Noted! Will plan accordingly!',
        options: [
            { value: 'full', label: 'Full Surprise! 🎉', icon: '🎁' },
            { value: 'within choices', label: 'Within My Choices 📋', icon: '✅' },
            { value: 'none', label: 'No Surprises Please 😌', icon: '📝' },
        ],
    },
    {
        key: 'beautyNails',
        chapter: 3,
        title: 'Nails?',
        description: 'Do you want to get your nails done?',
        assistantMessage: 'Self-care is important!',
        options: [
            { value: 'yes', label: 'Yes Please! 💅', icon: '💕' },
            { value: 'no', label: 'No Thanks', icon: '👋' },
            { value: 'not now', label: 'Not Now', icon: '⏳' },
        ],
        hasTextInput: true,
        textInputLabel: 'Preferred vendor (optional)',
        buildingSlot: 'F',
        buildingType: 'salon',
    },
    {
        key: 'beautyLashes',
        chapter: 3,
        title: 'Lashes?',
        description: 'Getting lashes done too?',
        assistantMessage: 'Beauty treatment time!',
        options: [
            { value: 'yes', label: 'Yes Please! 👁️', icon: '✨' },
            { value: 'no', label: 'No Thanks', icon: '👋' },
            { value: 'not now', label: 'Not Now', icon: '⏳' },
        ],
        hasTextInput: true,
        textInputLabel: 'Preferred vendor (optional)',
        buildingSlot: 'F',
        buildingType: 'beauty',
    },
    {
        key: 'spaAddon',
        chapter: 3,
        title: 'Spa Treatments',
        description: 'Interested in the spa?',
        assistantMessage: 'Relaxation station!',
        options: [
            { value: 'massage', label: 'Massage 💆', icon: '🧖' },
            { value: 'facial', label: 'Facial ✨', icon: '🧴' },
            { value: 'not interested', label: "I'm Good", icon: '👍' },
        ],
        buildingSlot: 'G',
        buildingType: 'spa',
    },
    {
        key: 'giftType',
        chapter: 3,
        title: 'Gift Preference',
        description: 'Do you have a preference?',
        assistantMessage: 'Final touch!',
        multiSelect: true,
        options: [
            { value: 'flowers', label: 'Flowers 💐', icon: '🌹' },
            { value: 'chocolate', label: 'Chocolate 🍫', icon: '🎀' },
            { value: 'money', label: 'Money 💰', icon: '💵' },
            { value: 'wig', label: 'Wig / Hair 💇‍♀️', icon: '💈' },
            { value: 'access', label: 'Bag / Shoes 👜', icon: '👠' },
            { value: 'gadget', label: 'Gadget 📱', icon: '⌚' },
            { value: 'perfume', label: 'Perfume 🌸', icon: '✨' },
            { value: 'jewelry', label: 'Jewelry 💎', icon: '💍' },
            { value: 'no gift needed', label: 'Just Your Love 💕', icon: '❤️' },
        ],
    },

    // Conditional quest
    {
        key: 'travelPlace',
        chapter: 2,
        title: 'Where To?',
        description: "What kind of destination?",
        assistantMessage: 'Exciting trip planning!',
        options: [
            { value: 'beach', label: 'Beach / Coastal 🏖️', icon: '🌊' },
            { value: 'mountains', label: 'Mountains / Nature 🏔️', icon: '🌲' },
            { value: 'city', label: 'City Adventure 🏙️', icon: '🌃' },
            { value: 'countryside', label: 'Countryside / Rural 🌾', icon: '🏡' },
            { value: 'staycation', label: 'Nearby Staycation 🛋️', icon: '🌴' },
            { value: 'other', label: 'Other...', icon: '✈️' },
        ],
        hasTextInput: true,
        textInputLabel: 'Tell me where! (if other)',
        condition: {
            questionKey: 'location',
            answerValue: ['travel', 'either is fine'],
        },
        buildingSlot: 'E',
        buildingType: 'station',
    },
];

import { QuestionConfig } from '../shared/types';

export function getQuestByKey(key: string): Quest | undefined {
    return QUESTS.find((q) => q.key === key);
}

export function applyQuestOverrides(overrides: QuestionConfig[]) {
    overrides.forEach(ov => {
        const q = QUESTS.find(q => q.key === ov.id);
        if (q) {
            if (ov.text) {
                q.title = ov.text;
                // Also update description? Optional.
            }
            if (ov.options && ov.options.length > 0) {
                // Map QuestionConfig options to QuestOption
                // Note: QuestOption needs 'icon' but QuestionConfig might not have it.
                // We'll try to preserve existing icons if possible or use default.
                q.options = ov.options.map((opt, idx) => ({
                    value: opt.value,
                    label: opt.label,
                    icon: q.options[idx]?.icon || '✨' // Fallback icon
                }));
            }
            q.disabled = ov.disabled;
        }
    });
}


export function getQuestsByChapter(chapter: 1 | 2 | 3): Quest[] {
    return QUESTS.filter((q) => q.chapter === chapter && !q.condition && !q.disabled);
}

export function getConditionalQuests(): Quest[] {
    return QUESTS.filter((q) => q.condition);
}

export function isQuestUnlocked(quest: Quest, answers: Record<string, string>): boolean {
    if (!quest.condition) return true;

    const required = quest.condition.answerValue;
    const actual = answers[quest.condition.questionKey];

    if (Array.isArray(required)) {
        return required.includes(actual);
    }

    return actual === required;
}

export function getNextQuest(
    answers: Record<string, string>,
    currentChapter: 1 | 2 | 3
): Quest | null {
    // Get quests for current chapter
    const chapterQuests = QUESTS.filter((q) => q.chapter === currentChapter && !q.disabled);

    for (const quest of chapterQuests) {
        // Skip if already answered
        if (answers[quest.key]) continue;

        // Check if conditional quest should be shown
        if (quest.condition) {
            if (isQuestUnlocked(quest, answers)) {
                return quest;
            }
            continue;
        }

        return quest;
    }

    return null;
}

export function isChapterComplete(chapter: 1 | 2 | 3, answers: Record<string, string>): boolean {
    const chapterQuests = QUESTS.filter((q) => q.chapter === chapter && !q.disabled);
    return chapterQuests.every((q) => {
        // If it's answered, great
        if (answers[q.key]) return true;

        // If it's not answered, check if it SHOULD be answered
        if (q.condition) {
            // If locked, we don't need to answer it -> verified as "complete"
            // If unlocked, we DO need to answer it -> not complete
            return !isQuestUnlocked(q, answers);
        }

        // Non-conditional quest not answered -> not complete
        return false;
    });
}

export function getCompletedQuestCount(answers: Record<string, string>): number {
    return QUESTS.filter((q) => !q.disabled && answers[q.key] && (!q.condition || isQuestUnlocked(q, answers))).length;
}
